import { applyTheme } from './applyTheme';
import { createHighlightController, TOKEN_TO_VARS } from './previewHighlight';
import type { HighlightController, HighlightToken } from './previewHighlight';
import type { RestaurantTheme } from '@/types';

const SOURCE = 'rt-theme-preview' as const;
const VERSION = 1 as const;

const THEME_KEYS = [
  'primary', 'primaryDeep', 'primaryTeal',
  'accent', 'footerGradientFrom', 'footerGradientTo',
] as const satisfies readonly (keyof RestaurantTheme)[];

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function isTheme(value: unknown): value is RestaurantTheme {
  if (!value || typeof value !== 'object') return false;
  return THEME_KEYS.every((k) => {
    const v = (value as Record<string, unknown>)[k];
    return typeof v === 'string' && HEX_RE.test(v);
  });
}

interface Envelope {
  source: typeof SOURCE;
  version: number;
  type: string;
  payload?: Record<string, unknown>;
}

function isEnvelope(d: unknown): d is Envelope {
  return !!d && typeof d === 'object' && (d as { source?: unknown }).source === SOURCE;
}

/**
 * Start the preview bridge. Returns a teardown function.
 * No-ops when the page isn't in an iframe or VITE_ADMIN_ORIGIN isn't configured.
 */
export function startPreviewBridge(initialSavedTheme: RestaurantTheme): () => void {
  if (typeof window === 'undefined') return () => {};
  if (window.top === window) return () => {};
  const adminOrigin = import.meta.env.VITE_ADMIN_ORIGIN as string | undefined;
  if (!adminOrigin) return () => {};

  const savedTheme = { ...initialSavedTheme };

  // Lazy-initialised highlight controller — created on first preview-highlight message.
  let controller: HighlightController | null = null;

  function ensureController() {
    if (!controller) {
      controller = createHighlightController();
    }
  }

  const post = (msg: unknown) => {
    window.parent.postMessage(msg, adminOrigin);
  };

  const handler = (event: MessageEvent) => {
    if (event.origin !== adminOrigin) return;
    if (!isEnvelope(event.data)) return;
    const { type, payload } = event.data;

    if (type === 'preview-theme') {
      const theme = (payload as { theme?: unknown } | undefined)?.theme;
      if (isTheme(theme)) {
        applyTheme(document.documentElement, theme);
      } else {
        post({ source: SOURCE, version: VERSION, type: 'preview-error',
               payload: { code: 'BAD_PAYLOAD', message: 'invalid theme' } });
      }
    } else if (type === 'preview-reset') {
      applyTheme(document.documentElement, savedTheme);
      // Invalidate highlight cache — theme may have changed element visibility.
      controller?.invalidate();
    } else if (type === 'preview-highlight') {
      const p = (payload as { token?: unknown; force?: unknown } | undefined);
      const token = p?.token;
      const force = p?.force === true;
      ensureController();
      if (token === null) {
        controller!.clear();
        post({ source: SOURCE, version: VERSION, type: 'preview-highlight-status',
               payload: { token: null, count: 0, scanning: false, index: 0 } });
      } else if (typeof token === 'string' && token in TOKEN_TO_VARS) {
        if (force) controller!.invalidate();
        post({ source: SOURCE, version: VERSION, type: 'preview-highlight-status',
               payload: { token, count: 0, scanning: true, index: 0 } });
        requestAnimationFrame(() => {
          const { count } = controller!.highlight(token as HighlightToken);
          post({ source: SOURCE, version: VERSION, type: 'preview-highlight-status',
                 payload: { token, count, scanning: false, index: 0 } });
        });
      } else {
        post({ source: SOURCE, version: VERSION, type: 'preview-error',
               payload: { code: 'BAD_TOKEN', message: 'unknown highlight token' } });
      }
    } else if (type === 'preview-highlight-focus') {
      const p = (payload as { token?: unknown; index?: unknown } | undefined);
      const token = p?.token;
      const index = typeof p?.index === 'number' ? p.index : 0;
      if (!controller) return;
      if (typeof token === 'string' && token in TOKEN_TO_VARS) {
        const { count, index: actualIdx } = controller.focus(token as HighlightToken, index);
        post({ source: SOURCE, version: VERSION, type: 'preview-highlight-status',
               payload: { token, count, scanning: false, index: actualIdx } });
      }
    }
  };

  window.addEventListener('message', handler);

  // Announce ready to admin parent.
  post({ source: SOURCE, version: VERSION, type: 'preview-ready', payload: { version: VERSION } });

  return () => window.removeEventListener('message', handler);
}
